import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { createBooking } from '../services/api';
import { BookingRequest } from '../types/api';

interface BookingFormProps {
  quoteId: number;
  onBookingComplete: () => void;
}

export const BookingForm: React.FC<BookingFormProps> = ({ quoteId, onBookingComplete }) => {
  const [bookingData, setBookingData] = useState<BookingRequest>({
    quoteId,
    referenceNumber: '',
    customerName: '',
    bookingDate: '',
  });

  const [location, setLocation] = useState('');
  const bookingMutation = useMutation({
    mutationFn: createBooking,
    onSuccess: () => {
      onBookingComplete();
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBookingData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setBookingData(prev => ({
        ...prev,
        bookingDate: date.toISOString()
      }));
    }
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLocation(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    bookingMutation.mutate(bookingData);
  };

  return (
    <div className="card bg-white shadow-lg rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-primary">Book Now</h2>
        {bookingMutation.isSuccess && (
          <div className="text-green-700 font-semibold">
            Confirmation Number: {bookingMutation.data.data.confirmationNumber}
          </div>
        )}
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reference Number
            </label>
            <input
              type="text"
              name="referenceNumber"
              value={bookingData.referenceNumber}
              onChange={handleInputChange}
              className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer Name
            </label>
            <input
              type="text"
              name="customerName"
              value={bookingData.customerName}
              onChange={handleInputChange}
              className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Booking Date
            </label>
            <DatePicker
              selected={bookingData.bookingDate ? new Date(bookingData.bookingDate) : null}
              onChange={handleDateChange}
              className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              dateFormat="MMMM d, yyyy"
              minDate={new Date()}
              placeholderText="Select a date"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <select
              value={location}
              onChange={handleLocationChange}
              className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              required
            >
              <option value="">City, State, or Zip</option>
              <option value="Charleston, SC 29401">Charleston, SC 29401</option>
              {/* Add more options as needed */}
            </select>
          </div>

          <div className="md:col-span-4 flex justify-end mt-2">
            <button
              type="submit"
              className="bg-primary text-white px-6 h-10 rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 text-base font-medium"
              disabled={bookingMutation.isPending}
            >
              {bookingMutation.isPending ? 'Processing...' : 'Book Now'}
            </button>
          </div>
        </div>

        {bookingMutation.isError && (
          <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
            An error occurred while processing your booking. Please try again.
          </div>
        )}
      </form>
    </div>
  );
};
